'use client';

/**
 * DesktopShell — the existing three-pane shell (explorer / canvas / copilot)
 * with the activity bar, header bar, status bar, and floating cart drawer.
 *
 * Unchanged from the pre-mobile-shell implementation — renamed from
 * `AppShell` so the top-level `app-shell.tsx` can branch between desktop
 * and mobile variants via `useIsMobile()`. Every prop, store interaction,
 * and layout rule is identical to what shipped before.
 *
 * @component_role: Desktop Shell
 * @component_type: Layout Container
 * @domain: Frontend Shell
 * @layer: presentation
 */

import { useEffect } from 'react';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { ActivityBar } from './activity-bar';
import { StatusBar } from './status-bar';
import { HeaderBar } from './header-bar';
import { EdgeStrip } from './edge-strip';
import { Explorer } from '@/components/explorer/explorer';
import { Canvas } from '@/components/canvas/canvas';
import { CopilotPanel } from '@/components/copilot/copilot-panel';
import { ChatDrawer } from '@/components/copilot/chat-drawer';
import { ChatSurface } from '@/components/copilot/chat/chat-surface';
import { ComposerSurface } from '@/components/copilot/composer/composer-surface';
import { InsightsPanel } from '@/components/copilot/rail-insights/insights-panel';
import { CostPlanSurface } from '@/components/copilot/rail-insights/cost-plan-surface';
import { AskSurface } from '@/components/copilot/rail-ask/ask-surface';
import { AskComposeSurface } from '@/components/copilot/rail-ask/ask-compose-surface';
import { AgentCanvasOverlay } from '@/components/agent-canvas/agent-canvas-overlay';
import { useAgentCanvasStore } from '@/lib/stores/agent-canvas-store';
import { useRailSurfaceStore } from '@/lib/stores/rail-surface-store';
import { cn } from '@/lib/utils/cn';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useSummonStateStore } from '@/lib/stores/summon-state-store';
import { useCopilotDefault, useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useVisiblePane } from '@/lib/shell/use-visible-pane';

export function DesktopShell() {
  const { showSecondarySidebar, showBottomPanel } = useLayoutStore();
  const activeMode = useLayoutStore((s) => s.activeExplorerView);
  // `isCanvasOnly` is derived from the shared `useVisiblePane` primitive so
  // mobile and desktop answer "which panes are visible" through one hook.
  // Today this collapses to canvas for the `home` landing view; tomorrow,
  // drawer/sheet open-state on mobile uses the same switch.
  const isCanvasOnly = useVisiblePane('desktop') === 'canvas';

  // Per-mode summon-state sync: when the active mode changes, apply the
  // user's saved per-mode Copilot posture (if they have one). Migration
  // sympathy — when no per-mode entry exists yet, leave
  // `showSecondarySidebar` alone so today's global preference carries
  // forward.
  // We read `copilot_default` as a stable primitive (not the full
  // ViewportConfig) so React's snapshot comparison stays cheap and
  // doesn't trigger the "result of getSnapshot should be cached" warning.
  // The wire field is still `copilot_default` (legacy key); on the
  // backend it now sources from `ModeSpec.rail_default` via the
  // Pydantic alias. Wire renames when CopilotPanel retires.
  const copilotDefault = useCopilotDefault(activeMode);
  useEffect(() => {
    const entry = useSummonStateStore.getState().byMode[activeMode];
    if (entry !== undefined && entry.copilot_open !== showSecondarySidebar) {
      useLayoutStore.setState({ showSecondarySidebar: entry.copilot_open });
    }
  }, [activeMode, copilotDefault, showSecondarySidebar]);

  // Rail visibility — `rail_default='absent'` modes (mode2's canvas-
  // only home) never render the rail; everything else is gated by the
  // user-controlled `showSecondarySidebar` toggle. The toggle defaults
  // to `true` in layout-store so non-absent modes start open; the
  // user can collapse the rail any time via the header toggle, and
  // the choice persists across reloads.
  //
  // `rail_default='summon'` is meant to start the rail collapsed by
  // default; that initial-state-per-mode behavior isn't fully wired
  // yet (no `summon` modes exist today). When it lands, it'll be a
  // useEffect that flips `showSecondarySidebar` on first entry to a
  // mode where the user has no saved per-mode preference.
  const railVisible =
    !isCanvasOnly && copilotDefault !== 'absent' && showSecondarySidebar;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Header Bar */}
      <HeaderBar />

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Activity Bar (far left rail) */}
        <ActivityBar />

        {/* Resizable Panels.
            `id` + `order` on each ResizablePanel are required when
            panels are conditionally rendered — without them the
            library can't redistribute width when a panel mounts /
            unmounts (canvas wouldn't reclaim the rail's space when
            the rail closes, etc.). */}
        <ResizablePanelGroup direction="horizontal" className="flex-1">
          {/* Primary Sidebar (Explorer) — hidden in canvas-only modes */}
          {!isCanvasOnly && (
            <>
              <ResizablePanel
                id="shell-explorer"
                order={1}
                defaultSize={20}
                minSize={15}
                maxSize={35}
                className="bg-sidebar"
              >
                <Explorer />
              </ResizablePanel>

              <ResizableHandle withHandle />
            </>
          )}

          {/* Main Content + Optional Bottom Panel */}
          <ResizablePanel
            id="shell-canvas"
            order={2}
            defaultSize={isCanvasOnly ? 100 : railVisible ? 55 : 80}
          >
            <ResizablePanelGroup direction="vertical">
              {/* Canvas (Editor Area) — wrapped in a relatively-positioned
                  container so AgentCanvasOverlay (rail-side Ask Klyde's
                  rich-answer surface) can absolute-position over the
                  canvas pane only. Rail/Explorer/ActivityBar stay
                  outside the relative container, so the overlay never
                  obscures them. Canvas tree stays mounted underneath
                  the overlay's backdrop — close returns to the prior
                  canvas state untouched (no unmount, no scroll loss,
                  no re-fetch). */}
              <ResizablePanel
                id="shell-canvas-main"
                order={1}
                defaultSize={showBottomPanel ? 70 : 100}
              >
                <CanvasWithAgentOverlay />
              </ResizablePanel>

              {/* Bottom Panel (optional) */}
              {showBottomPanel && (
                <>
                  <ResizableHandle withHandle />
                  <ResizablePanel
                    id="shell-canvas-bottom"
                    order={2}
                    defaultSize={30}
                    minSize={15}
                  >
                    <div className="h-full bg-card p-4">
                      <div className="text-sm text-muted-foreground">
                        Bottom Panel (Logs / Context)
                      </div>
                    </div>
                  </ResizablePanel>
                </>
              )}
            </ResizablePanelGroup>
          </ResizablePanel>

          {/* Secondary Sidebar (Copilot) — hidden in canvas-only modes.
              Wraps CopilotPanel + ChatDrawer in a relatively-positioned
              container so the drawer-over pattern works: ChatDrawer
              uses `absolute inset-0` to fully occlude Copilot when
              chatDrawerOpen is true. Step 4 of the chrome direction. */}
          {railVisible && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel
                id="shell-rail"
                order={3}
                defaultSize={25}
                minSize={20}
                maxSize={40}
              >
                {/* Phase 1 of right-rail UX direction: two peer
                    surfaces (Insights, Copilot) share this column,
                    one visible at a time, switched via EdgeStrip
                    icons. Both stay mounted so React Query keeps
                    each surface's data warm; switching is a CSS
                    toggle. ChatDrawer continues to overlay both via
                    `absolute inset-0` (existing pattern). See
                    klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md. */}
                <RailColumn />
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>

        {/* Edge Strip — right-edge summon column. Always visible alongside
            the resizable group when the mode supports Copilot at all
            (`copilot_default !== 'absent'`). Step 2 of the chrome direction;
            today only one icon (Copilot toggle), step 4 will add Chat as a
            second icon. See klyde_edge/docs/copilot_chat_chrome_concept.md. */}
        {!isCanvasOnly && <EdgeStrip />}
      </div>

      {/* Status Bar */}
      <StatusBar />

      {/* The legacy floating cart drawer is retired — the rail Composer
          surface is the one cart UI everywhere now. Its last real caller,
          the obeya RFQ-comparison "Award Vendor" flow, now opens the rail
          composer directly (see openComposerSurface in rail-surface-store.ts)
          rather than the drawer. */}
    </div>
  );
}

/**
 * CanvasWithAgentOverlay — wraps the matrix-driven Canvas with the
 * agent canvas overlay (the concertina rich-answer surface from the
 * rail-side Ask Klyde).
 *
 * When the overlay is open, the underlying Canvas tree stays mounted
 * (no unmount → no scroll loss, no re-fetch on close) but is visibly
 * dimmed via a brightness filter and made non-interactive — clicks
 * fall through to the overlay's invisible click-catcher backdrop,
 * which steps the concertina detent down by one.
 *
 * Subscribing to the agent-canvas store here (rather than inside
 * <AgentCanvasOverlay />) lets us apply the brightness filter to the
 * Canvas wrapper without modifying Canvas itself — Canvas remains
 * the matrix-driven peer it always was.
 */
function CanvasWithAgentOverlay() {
  const overlayOpen = useAgentCanvasStore((s) => s.detent !== 'closed');
  return (
    <div className="relative h-full">
      <div
        className={cn(
          'h-full transition-[filter] duration-200',
          overlayOpen && 'pointer-events-none brightness-50',
        )}
      >
        <Canvas />
      </div>
      <AgentCanvasOverlay />
    </div>
  );
}

/**
 * RailColumn — hosts the peer surfaces (Insights, Copilot, Composer,
 * Chat) + the legacy chat overlay fallback. All peer surfaces stay
 * mounted so their data stays warm across rail-icon switches;
 * visibility is a CSS hide driven by `useRailSurfaceStore.active`.
 * Wrapping divs use `absolute inset-0` so the inactive surface
 * contributes no flow height — toggling doesn't shift the active
 * surface around.
 *
 * Chat surface: when the active (mode, selection) declares
 * `chat_surface_cards` in the matrix, the registry-driven
 * `<ChatSurface />` mounts as a fourth peer (no longer an overlay).
 * For modes that haven't declared chat cards yet, the legacy
 * monolithic `<ChatDrawer />` keeps rendering as an overlay
 * (gated internally by `chatDrawerOpen`). Per-mode migration —
 * mode1/mode4 are the first to opt in; obeya/vendors/etc. stay
 * on the legacy overlay until each declares its own card list.
 */
function RailColumn() {
  const active = useRailSurfaceStore((s) => s.active);
  const { mode, contentSelection: selection } = useViewportSelection();
  const getViewportConfig = useTotalMatrixStore((s) => s.getViewportConfig);
  // Subscribe to `matrix` so the cfg-derived structure below re-resolves
  // once the matrix finishes loading (getViewportConfig is a stable ref).
  const matrix = useTotalMatrixStore((s) => s.matrix);
  const cfg = matrix ? getViewportConfig(mode, selection) : null;
  const useRegistryChat = (cfg?.chat_surface_cards?.length ?? 0) > 0;
  // Legacy Copilot tab-panel — mounted only for modes that declare
  // copilot `tabs` in the matrix. Registry-driven modes (mode1-8) carry
  // `tabs: []` for every selection (`_selection_from_preset`), so the
  // legacy Copilot surface drops off the cards-everywhere modes — and
  // off each legacy mode as it migrates to the registry.
  const hasCopilot = (cfg?.copilot?.length ?? 0) > 0;
  // Never leave the rail blank: if the persisted active surface is
  // unavailable for this (mode, selection), show Insights instead.
  // Two cases: 'copilot' on a registry-driven mode, 'chat' on a mode
  // that hasn't declared chat_surface_cards. ('ask' is always
  // available — no matrix gate, parallel to Insights.)
  const effectiveActive =
    (active === 'copilot' && !hasCopilot) ||
    (active === 'chat' && !useRegistryChat)
      ? 'insights'
      : active;

  return (
    <div className="relative h-full">
      <div className={cn('absolute inset-0', effectiveActive !== 'insights' && 'hidden')}>
        <InsightsPanel />
      </div>
      {/* AskSurface — agent peer rail surface. Always mounted so the
          thread + React Query cache stay warm across rail-icon
          switches; visibility is a CSS toggle keyed off
          `useRailSurfaceStore.active === 'ask'`. Parallel to mode8's
          canvas-resident DemoAskInputCard — same ki_ask backend,
          separate frontend so mode8 stays untouched. */}
      <div className={cn('absolute inset-0', effectiveActive !== 'ask' && 'hidden')}>
        <AskSurface />
      </div>
      {/* AskComposeSurface — the klyde_ask compositor peer (federated lanes).
          Parallel to AskSurface; visibility keyed off active === 'compose'. */}
      <div className={cn('absolute inset-0', effectiveActive !== 'compose' && 'hidden')}>
        <AskComposeSurface />
      </div>
      {hasCopilot && (
        <div className={cn('absolute inset-0', effectiveActive !== 'copilot' && 'hidden')}>
          <CopilotPanel />
        </div>
      )}
      {/* ComposerSurface — peer rail surface (cart-as-stack).
          Always mounted so cart-store data stays warm; visibility is
          a CSS toggle keyed off `useRailSurfaceStore.active`. */}
      <div className={cn('absolute inset-0', effectiveActive !== 'composer' && 'hidden')}>
        <ComposerSurface />
      </div>
      {/* CostPlanSurface — peer rail surface showing the PINNED cost
          plan's line items (mcp_details card, parameterized by the
          pin). Always mounted so the pin + its data stay warm;
          visibility is a CSS toggle keyed off
          `useRailSurfaceStore.active === 'cost_plan'`. Mode-gated
          internally via `cost_plan_surface_cards` (mode3/mode11
          today) — self-renders an "unavailable" state elsewhere,
          same pattern as ComposerSurface. */}
      <div className={cn('absolute inset-0', effectiveActive !== 'cost_plan' && 'hidden')}>
        <CostPlanSurface />
      </div>
      {/* ChatSurface — peer rail surface (registry-driven chat).
          Mounted only when this (mode, selection) declares chat cards
          in the matrix; otherwise the legacy <ChatDrawer /> overlay
          below handles chat for that mode during cohabitation. */}
      {useRegistryChat && (
        <div className={cn('absolute inset-0', effectiveActive !== 'chat' && 'hidden')}>
          <ChatSurface />
        </div>
      )}
      {/* Legacy chat overlay — for modes that haven't migrated to the
          registry-driven chat peer yet. ChatDrawer self-gates on
          `chatDrawerOpen`; for migrated modes the edge-strip routes
          the chat icon through setActiveSurface('chat') instead, so
          this overlay never opens. */}
      {!useRegistryChat && <ChatDrawer />}
    </div>
  );
}
